<script setup>
/**
 * key 运行时（S7）—— 一页回答三个问题，全部靠状态回答，页面上不写解释：
 *   1. 审计队列在丢数据吗？「溢出丢弃」> 0 时这张卡是红的（`audit_queue.overflow` 是唯一信号）。
 *   2. 哪一把 key 一直在认证失败？状态列打「认证失败中」，认证失败次数标红。
 *   3. 冷却还剩多久？冷却列是秒级倒计时（接口给取数那一刻的剩余，界面按经过时间自己减）。
 *
 * 「用量」是本副本的进程内存（keyrouter router.js:71），当前请求数 / 冷却 / TPM / RPM 来自 Redis ——
 * 这条区别只进表头的 `title`，不占页面。
 */
import { computed, onMounted, onUnmounted, ref } from 'vue'
import {
  Badge,
  Button,
  ConfirmAction,
  EmptyState,
  PageHeader,
  Section,
  Spinner,
  Stat,
  Table,
  useToast,
} from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import {
  fetchModelCapabilityCatalog,
  fetchProviderCapabilityKeys,
  fetchServiceKeys,
  saveProviderCapabilityKey,
  saveServiceKey,
} from '../../api/model-capabilities.js'
import { clearKeyCooldown, listKeyRuntime } from '../../api/llm/index.js'
import DramaAccounts from '../../components/novel/DramaAccounts.vue'
import { keyFieldsOf, toKeyEnabledBody } from './llm-key-form.js'
import {
  cooldownRemaining,
  formatCooldown,
  formatCount,
  formatPercent,
  healthVariant,
  keyHealth,
  capabilityFromVendor,
  saturationOf,
  SERVICE_KEY_CAPABILITIES,
} from './key-runtime.js'

const { t } = useI18n()
const toast = useToast()
const router = useRouter()

/** 后端 `stats()` 每把 key 要 2 次串行 Redis 往返，50 把就是 ~100ms。1 秒轮询会把 Redis 打穿。 */
const REFRESH_MS = 5000

/** @type {import('vue').Ref<import('../../api/llm/index.js').KeyRuntimeRow[]>} */
const rows = ref([])
/** @type {import('vue').Ref<import('../../api/llm/index.js').AuditQueue|null>} */
const auditQueue = ref(null)
const loading = ref(false)
const error = ref('')
const actionError = ref('')
/** 上一次失败的操作，供错误条上的「重试」重跑。 */
const lastAction = ref(null)
const retrying = ref(false)
/** 取数那一刻，和当前时刻 —— 两者之差就是冷却要减掉的时间。 */
const fetchedAt = ref(0)
const nowMs = ref(Date.now())

let refreshTimer = null
let tickTimer = null

onMounted(() => {
  load()
  refreshTimer = setInterval(load, REFRESH_MS)
  tickTimer = setInterval(() => (nowMs.value = Date.now()), 1000)
})

onUnmounted(() => {
  clearInterval(refreshTimer)
  clearInterval(tickTimer)
})

/** 每行加上「此刻」的派生量：冷却是走动的，健康与饱和度跟着它变。 */
const keys = computed(() =>
  rows.value.map((row) => {
    const cooldownS = cooldownRemaining(row.cooldown_remaining_s, nowMs.value - fetchedAt.value)
    return { ...row, cooldownS, health: keyHealth(row, cooldownS), saturation: saturationOf(row) }
  }),
)

const columns = computed(() => [
  { key: 'vendor', label: t('visualGameAdmin.llm.runtime.table.vendor'), width: '100px' },
  { key: 'label', label: t('visualGameAdmin.llm.runtime.table.label'), width: '150px' },
  { key: 'masked', label: t('visualGameAdmin.llm.runtime.table.masked'), width: '160px', mono: true },
  { key: 'health', label: t('visualGameAdmin.llm.runtime.table.health'), width: '90px' },
  { key: 'concurrency', label: t('visualGameAdmin.llm.runtime.table.concurrency'), width: '100px', align: 'right', mono: true },
  { key: 'saturation', label: t('visualGameAdmin.llm.runtime.table.saturation'), width: '90px', align: 'right', mono: true },
  { key: 'tpm', label: t('visualGameAdmin.llm.runtime.table.tpm'), width: '100px', align: 'right', mono: true },
  { key: 'rpm', label: t('visualGameAdmin.llm.runtime.table.rpm'), width: '90px', align: 'right', mono: true },
  { key: 'cooldown', label: t('visualGameAdmin.llm.runtime.table.cooldown'), width: '100px', align: 'right', mono: true },
  {
    key: 'authFailures',
    label: t('visualGameAdmin.llm.runtime.table.authFailures'),
    hint: t('visualGameAdmin.llm.runtime.table.authFailuresHint'),
    width: '110px',
    align: 'right',
    mono: true,
  },
  {
    key: 'usage',
    label: t('visualGameAdmin.llm.runtime.table.usage'),
    hint: t('visualGameAdmin.llm.runtime.table.usageHint'),
    width: '90px',
  },
  { key: 'actions', label: t('visualGameAdmin.llm.runtime.table.actions'), width: '100px' },
])

const updatedAt = computed(() =>
  fetchedAt.value === 0 ? '' : new Date(fetchedAt.value).toLocaleTimeString(),
)

/** 表格自己带边框和内边距，Section 不再包一层；空态和错误条各自加内边距。 */
const none = computed(() => t('visualGameAdmin.llm.runtime.values.none'))

async function load() {
  loading.value = true
  try {
    const snapshot = await listKeyRuntime()
    rows.value = snapshot.items
    auditQueue.value = snapshot.audit_queue
    fetchedAt.value = Date.now()
    nowMs.value = fetchedAt.value
    error.value = ''
  } catch (err) {
    error.value = err.message
  } finally {
    loading.value = false
  }
}

/** 上限 `null` = 没设上限。`2,100 / —` 读作「跑了 2,100，没有天花板」。 */
function loadOf(current, limit) {
  return `${formatCount(current)} / ${limit == null ? none.value : formatCount(limit)}`
}

/** @param {Record<string, number>} totals */
function usageText(totals) {
  const parts = []
  for (const [name, value] of Object.entries(totals)) {
    parts.push(`${name} ${formatCount(value)}`)
  }
  return parts.length === 0 ? none.value : parts.join(' · ')
}

/**
 * 把一次写操作包成 `ConfirmAction` 要的 thunk，并连同「成功之后干什么」一起记下来供重试
 * ——只记 thunk 的话，重试成功后弹的会是另一个操作的提示。
 * @param {() => Promise<unknown>} run
 * @param {() => void} onDone
 */
function confirmable(run, onDone) {
  return async () => {
    actionError.value = ''
    lastAction.value = { run, onDone }
    await run()
  }
}

/**
 * 停用一把 key：运行时行里没有 weight / max_concurrency 这些配置列，按能力接入读取密钥池中的完整行，
 * 只翻 enabled 提交回去。取不回来就抛 —— 不拿默认值凑一行出来覆盖真配置。
 * @param {import('../../api/llm/index.js').KeyRuntimeRow} row
 */
async function disableKey(row) {
  const { capability, code } = capabilityFromVendor(row.vendor)
  if (SERVICE_KEY_CAPABILITIES.includes(capability)) {
    const { pools } = await fetchServiceKeys()
    const serviceKey = pools.find(pool => pool.vendor === row.vendor)?.keys.find(key => String(key.id) === row.key_id)
    if (!serviceKey) throw new Error(`API Key ${row.key_id} 不属于 ${row.vendor}`)
    await saveServiceKey(row.vendor, toKeyEnabledBody(serviceKey, false, keyFieldsOf(row.vendor)))
    return
  }
  const catalog = await fetchModelCapabilityCatalog()
  const providerCapability = catalog[capability].provider_capabilities
    .find(item => item.code === code)
  if (!providerCapability) throw new Error(`能力接入不存在: ${capability}/${code}`)
  const keyPool = await fetchProviderCapabilityKeys(providerCapability.id)
  const keyRow = keyPool.keys.find(key => String(key.id) === row.key_id)
  if (!keyRow) throw new Error(`API Key ${row.key_id} 不属于模型能力 ${capability}/${code}`)
  await saveProviderCapabilityKey(providerCapability.id, toKeyEnabledBody(keyRow, false, keyFieldsOf(row.vendor)))
}

/**
 * vendor → 「能力 · 接入代码」（如 `文本 · volcengine` / `图片 · seedream`）。
 * 四种能力的 key 同表展示，只显示 code 分不清 text 和媒体的同名接入。
 * @param {string} vendor
 */
function vendorLabel(vendor) {
  const { capability, code } = capabilityFromVendor(vendor)
  return `${t(`visualGameAdmin.llm.capabilities.types.${capability}`)} · ${code}`
}

function onCooldownCleared() {
  actionError.value = ''
  lastAction.value = null
  toast.success(t('visualGameAdmin.llm.runtime.toast.cooldownCleared'))
  load()
}

function onKeyDisabled() {
  actionError.value = ''
  lastAction.value = null
  toast.success(t('visualGameAdmin.llm.runtime.toast.disabled'))
  load()
}

/** @param {Error} err */
function onActionFailed(err) {
  actionError.value = err.message
  toast.error(t('visualGameAdmin.llm.runtime.toast.failed'), { duration: 0 })
}

async function retryLast() {
  if (!lastAction.value) throw new Error('Runtime.retryLast: 没有失败的操作可重试')
  const { run, onDone } = lastAction.value
  retrying.value = true
  try {
    await run()
    onDone()
  } catch (err) {
    onActionFailed(err)
  } finally {
    retrying.value = false
  }
}
</script>

<template>
  <div
    class="llm-runtime vga-page"
    data-insp-path="apps/visual-game-admin/src/pages/llm/Runtime.vue#page"
  >
    <PageHeader :title="t('visualGameAdmin.llm.runtime.title')">
      <template #actions>
        <span
          v-if="updatedAt"
          class="llm-runtime__updated"
        >
          {{ t('visualGameAdmin.llm.runtime.refresh.updatedAt', { time: updatedAt }) }}
        </span>
        <Button
          variant="secondary"
          size="sm"
          :loading="loading"
          @click="load"
        >
          {{ t('visualGameAdmin.llm.runtime.refresh.now') }}
        </Button>
      </template>
    </PageHeader>

    <Section
      v-if="auditQueue"
      :title="t('visualGameAdmin.llm.runtime.audit.title')"
      data-insp-path="apps/visual-game-admin/src/pages/llm/Runtime.vue#audit-queue"
    >
      <div class="llm-runtime__stats">
        <Stat
          :label="t('visualGameAdmin.llm.runtime.audit.queued')"
          :value="formatCount(auditQueue.queued)"
        />
        <Stat
          :label="t('visualGameAdmin.llm.runtime.audit.queuedBytes')"
          :value="formatCount(auditQueue.queued_bytes)"
        />
        <Stat
          :label="t('visualGameAdmin.llm.runtime.audit.overflow')"
          :value="formatCount(auditQueue.overflow)"
          :tone="auditQueue.overflow > 0 ? 'danger' : 'neutral'"
        />
        <Stat
          :label="t('visualGameAdmin.llm.runtime.audit.retryCount')"
          :value="formatCount(auditQueue.retry_count)"
        />
      </div>
    </Section>

    <DramaAccounts />

    <Section
      :title="t('visualGameAdmin.llm.runtime.keys.title')"
      :padded="false"
      data-insp-path="apps/visual-game-admin/src/pages/llm/Runtime.vue#keys"
    >
      <div
        v-if="error"
        class="llm-runtime__error"
        role="alert"
      >
        <p class="vga-error">
          {{ error }}
        </p>
        <Button
          variant="secondary"
          size="sm"
          :loading="loading"
          @click="load"
        >
          {{ t('visualGameAdmin.common.retry') }}
        </Button>
      </div>

      <div
        v-if="actionError"
        class="llm-runtime__error"
        role="alert"
      >
        <p class="vga-error">
          {{ actionError }}
        </p>
        <Button
          variant="secondary"
          size="sm"
          :loading="retrying"
          @click="retryLast"
        >
          {{ t('visualGameAdmin.common.retry') }}
        </Button>
      </div>

      <div
        v-if="loading && fetchedAt === 0"
        class="llm-runtime__pad llm-runtime__loading"
      >
        <Spinner :label="t('visualGameAdmin.common.loading')" />
        <span>{{ t('visualGameAdmin.common.loading') }}</span>
      </div>

      <div
        v-else-if="keys.length === 0"
        class="llm-runtime__pad"
      >
        <EmptyState :title="t('visualGameAdmin.llm.runtime.empty.title')">
          <template #action>
            <Button
              variant="primary"
              @click="router.push({ name: 'llm-providers' })"
            >
              {{ t('visualGameAdmin.llm.runtime.empty.action') }}
            </Button>
          </template>
        </EmptyState>
      </div>

      <Table
        v-else
        :columns="columns"
        :rows="keys"
        row-key="key_id"
      >
        <template #vendor="{ row }">
          {{ vendorLabel(row.vendor) }}
        </template>
        <template #label="{ row }">
          <span :title="row.label ?? none">{{ row.label ?? none }}</span>
        </template>
        <template #masked="{ row }">
          <span :title="row.api_key_masked">{{ row.api_key_masked }}</span>
        </template>
        <template #health="{ row }">
          <Badge :variant="healthVariant(row.health)">
            {{ t(`visualGameAdmin.llm.runtime.health.${row.health}`) }}
          </Badge>
        </template>
        <template #concurrency="{ row }">
          {{ row.in_flight }} / {{ row.max_concurrency }}
        </template>
        <template #saturation="{ row }">
          <span :class="{ 'is-hot': row.saturation !== null && row.saturation >= 0.9 }">
            {{ row.saturation === null ? none : formatPercent(row.saturation) }}
          </span>
        </template>
        <template #tpm="{ row }">
          {{ loadOf(row.tpm, row.max_tpm) }}
        </template>
        <template #rpm="{ row }">
          {{ loadOf(row.rpm, row.max_rpm) }}
        </template>
        <template #cooldown="{ row }">
          <span :class="{ 'is-cooling': row.cooldownS > 0 }">
            {{ row.cooldownS === 0 ? none : formatCooldown(row.cooldownS) }}
          </span>
        </template>
        <template #authFailures="{ row }">
          <span :class="{ 'is-hot': row.auth_failures_24h > 0 }">
            {{ formatCount(row.auth_failures_24h) }}
          </span>
        </template>
        <template #usage="{ row }">
          <span
            class="llm-runtime__usage"
            :title="t('visualGameAdmin.llm.runtime.table.usageScope')"
          >
            {{ usageText(row.usage.totals) }}
          </span>
        </template>
        <template #actions="{ row }">
          <div class="llm-runtime__actions">
            <ConfirmAction
              v-if="row.cooldownS > 0"
              :label="t('visualGameAdmin.llm.runtime.actions.clearCooldown')"
              :confirm-text="t('visualGameAdmin.llm.runtime.actions.confirmClearCooldown')"
              :cancel-label="t('visualGameAdmin.common.cancel')"
              :ok-label="t('visualGameAdmin.common.confirm')"
              :pending-label="t('visualGameAdmin.common.loading')"
              :action="confirmable(() => clearKeyCooldown(row.key_id), onCooldownCleared)"
              @done="onCooldownCleared"
              @error="onActionFailed"
            />
            <ConfirmAction
              danger
              :label="t('visualGameAdmin.llm.runtime.actions.disable')"
              :confirm-text="t('visualGameAdmin.llm.runtime.actions.confirmDisable')"
              :cancel-label="t('visualGameAdmin.common.cancel')"
              :ok-label="t('visualGameAdmin.common.confirm')"
              :pending-label="t('visualGameAdmin.common.loading')"
              :action="confirmable(() => disableKey(row), onKeyDisabled)"
              @done="onKeyDisabled"
              @error="onActionFailed"
            />
          </div>
        </template>
      </Table>
    </Section>
  </div>
</template>

<style scoped>
.llm-runtime__updated {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
  white-space: nowrap;
}

.llm-runtime__stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: var(--drama-space-lg);
}

.llm-runtime__pad {
  padding: var(--drama-space-xl);
}

.llm-runtime__error {
  display: flex;
  align-items: center;
  gap: var(--drama-space-md);
  margin: var(--drama-space-lg);
  padding: var(--drama-space-md);
  border: 1px solid var(--drama-color-danger);
  border-radius: var(--drama-radius-md);
  background: color-mix(in srgb, var(--drama-color-danger) 10%, transparent);
}

.llm-runtime__error p {
  flex: 1;
  min-width: 0;
  margin: 0;
}

.llm-runtime__loading {
  display: flex;
  align-items: center;
  gap: var(--drama-space-md);
  color: var(--drama-color-text-muted);
}

.llm-runtime__usage {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
}

.llm-runtime__actions {
  display: flex;
  align-items: center;
  gap: var(--drama-space-sm);
}

.is-hot {
  color: var(--drama-color-danger);
}

.is-cooling {
  color: var(--drama-color-warning);
}
</style>
